import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useEventCallback from 'hooks/useEventCallback';
import { BigNumberJs } from 'lib/BigNumber';

import { LevelTwoOrderBookRow } from './orderBookServiceUtils';
import { HoveredRow } from './types';

export const useSummaryLists = ({
  asks,
  bids,
  type,
}: {
  asks: LevelTwoOrderBookRow[];
  bids: LevelTwoOrderBookRow[];
  type: 'ask' | 'bid' | undefined;
}) => {
  const { rowsSumAsks, rowsSumBids } = useMemo(() => {
    // Only start recalculations after user first time hover over
    if (!type) {
      return {};
    }
    const rows = type === 'ask' ? asks.slice(0).reverse() : bids;
    let sumTotal = BigNumberJs(0);
    let sumQuantity = BigNumberJs(0);
    const sums = rows.map(row => {
      sumTotal = sumTotal.plus(row.total);
      sumQuantity = sumQuantity.plus(row.quantity);
      return { sumTotal, sumQuantity };
    });
    if (type === 'ask') {
      return { rowsSumAsks: sums.reverse() };
    }
    return { rowsSumBids: sums };
  }, [type, asks, bids]);

  return { rowsSumAsks, rowsSumBids };
};

export const useHoveredRow = () => {
  const [lastHoveredType, setLastHoveredType] = useState<
    'ask' | 'bid' | undefined
  >();
  const [hoveredRow, setHoveredRow] = useState<HoveredRow | undefined>(
    undefined
  );

  useEffect(() => {
    if (!hoveredRow?.type) {
      return;
    }
    if (lastHoveredType !== hoveredRow.type) {
      // Track last hovered type to optimize recalculations on either side
      // TODO: could degrade performance
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastHoveredType(hoveredRow.type);
    }
  }, [hoveredRow, lastHoveredType]);

  const onHoverExit = useCallback(() => setHoveredRow(undefined), []);

  const xOffsetRef = useRef<HTMLDivElement>(null);
  const [xOffset, setXOffset] = useState<number | undefined>(undefined);

  const checkOffset = useEventCallback(() => {
    const newOffset = xOffsetRef.current?.getBoundingClientRect().x;
    if (newOffset !== xOffset) {
      setXOffset(newOffset);
    }
  });

  useEffect(() => {
    checkOffset();
    setTimeout(checkOffset, 1000);
  }, [checkOffset]);

  useEffect(() => {
    window.addEventListener('resize', checkOffset);
    return () => {
      window.removeEventListener('resize', checkOffset);
    };
  }, [checkOffset, xOffset]);

  return {
    onRowHover: setHoveredRow,
    lastHoveredType,
    hoveredRow,
    handleRowHoverExit: onHoverExit,
    xOffset,
    xOffsetRef,
  };
};
